/**
 * Nome do arquivo que o SERVIDOR mandou salvar (E65.F6.C04 — F-E65.F6-04).
 *
 * O backend sanea o nome (RFC 6266) e corrige a extensão pelo mime que validou
 * na ingestão: um upload chamado `evidencia.exe` com bytes PNG desce como
 * `evidencia.png`. Se o cliente ignorar o header e usar o `original_filename`
 * da listagem, o navegador grava de volta o `.exe` e a garantia do servidor
 * morre no último metro.
 *
 * O parser lê `filename*` antes de `filename`: é ele que carrega o nome com
 * acento, percent-encoded em UTF-8. Sem header legível, devolve o fallback —
 * baixar com um nome razoável é melhor do que não baixar.
 */

/**
 * O que todo download de documento emitido devolve (E85.F2 — GUS-32).
 *
 * `codigoVerificacao` é o SHA-256 dos bytes ENTREGUES, lido do cabeçalho que o
 * servidor calcula sobre o mesmo conteúdo que gravou na resposta — a mesma
 * conta da cadeia de custódia. `null` quando o servidor não o enviou (resposta
 * antiga ou dublada): a tela então não mostra nada, em vez de inventar.
 */
export interface DocumentoBaixado {
  blob: Blob;
  codigoVerificacao: string | null;
}

export interface ArquivoBaixado {
  blob: Blob;
  /** Nome a usar no `download` do link — o do servidor, quando ele o envia. */
  filename: string;
  /**
   * Opcional aqui: o documento ORIGINAL baixado do caso não é emissão, e o seu
   * código já mora na cadeia de custódia — só os documentos emitidos o levam.
   */
  codigoVerificacao?: string | null;
}

/** O cabeçalho é declarado uma vez no backend (`http_files`); o axios o entrega em minúsculas. */
export const CABECALHO_CODIGO_DE_VERIFICACAO = 'x-documento-sha256';

const _SHA256_HEX = /^[0-9a-f]{64}$/i;

/**
 * O código de verificação do arquivo que acabou de descer, ou `null`.
 *
 * Só aceita um SHA-256 em hexadecimal: um valor truncado ou de outro formato
 * seria mostrado como se fosse o código, e a pessoa o compararia com o
 * `sha256sum` do arquivo sem nunca bater.
 */
export function codigoDeVerificacao(headers: unknown): string | null {
  if (typeof headers !== 'object' || headers === null) return null;
  const registro = headers as Record<string, unknown>;
  const valor =
    registro[CABECALHO_CODIGO_DE_VERIFICACAO] ??
    registro[CABECALHO_CODIGO_DE_VERIFICACAO.toUpperCase()];
  if (typeof valor !== 'string') return null;
  const limpo = valor.trim().toLowerCase();
  return _SHA256_HEX.test(limpo) ? limpo : null;
}

const _ESTENDIDO = /filename\*\s*=\s*UTF-8''([^;]+)/i;
const _SIMPLES = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/i;

export function nomeDoAnexo(contentDisposition: unknown, fallback: string): string {
  if (typeof contentDisposition !== 'string') return fallback;

  const estendido = _ESTENDIDO.exec(contentDisposition);
  if (estendido?.[1]) {
    try {
      const decodificado = decodeURIComponent(estendido[1].trim());
      if (decodificado) return decodificado;
    } catch {
      // Percent-encoding quebrado: cai no `filename` simples abaixo em vez de
      // derrubar o download inteiro por causa do nome.
    }
  }

  const simples = _SIMPLES.exec(contentDisposition);
  const bruto = (simples?.[1] ?? simples?.[2] ?? '').trim();
  return bruto || fallback;
}

/**
 * Entrega ao navegador o arquivo já baixado, com o nome que o servidor mandou.
 *
 * O link temporário é o único caminho que preserva o NOME: `window.open` sobre
 * uma `blob:` URL salva com o identificador aleatório da URL. O `revokeObjectURL`
 * fecha o vazamento de memória que sobra quando a página vive (a lista de casos
 * fica aberta e a pessoa baixa vários documentos seguidos).
 */
export function salvarArquivoBaixado(arquivo: ArquivoBaixado): void {
  const url = URL.createObjectURL(arquivo.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = arquivo.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
